import { useQuery } from '@apollo/client';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { runErrorMessage } from './scheduleFormat.ts';
import { forgetWatchedTestRun, getWatchedTestRun } from './useScheduleTestRun.ts';
import { useToaster } from '../../../core/components/Toast.tsx';
import { skipPollAttemptWhenHidden } from '../../../core/utils/hooks/useRefetchOnTabVisible.ts';
import { url } from '../../../core/utils/link.ts';
import { AUTOMATION_PATH, CONVERSATION_PATH, SCHEDULE_RUNS_UPDATE_INTERVAL } from '../../constants.ts';
import { GET_CONVERSATION_SCHEDULES } from '../../queries.ts';

// { [scheduleId]: lastRunAt of the last run the user was told about }
const STORAGE_KEY = 'chat.scheduleRunsSeen';
type Seen = Record<string, string>;

const readSeen = (): Seen => {
    try {
        const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');
        return value && typeof value === 'object' ? value : {};
    } catch {
        return {};
    }
};

const writeSeen = (seen: Seen) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(seen));
    } catch {
        // storage full or blocked: the toasts then only dedupe within this tab's lifetime
    }
};

const isNewer = (value: string, than?: string) => !than || new Date(value).getTime() > new Date(than).getTime();

interface ScheduleRunToastsOptions {
    // false where the chat is off, as the hook is called before ChatPage's early return
    enabled?: boolean;
}

// navigation state for the automation's page: open its edit dialog on arrival (the toast's Fix)
export const EDIT_SCHEDULE_STATE = { editSchedule: true };

/**
 * Toasts the outcome of the user's schedule runs (ENG-11296), mounted once in the chat module. Polls the schedules
 * (the same query the sidebar reads, so the poll refreshes it too) and compares each schedule's `lastRunAt` with the
 * last one seen; the first load only records. A run that auto-paused its schedule gets a sticky toast with Fix, a
 * failed run an error toast, a succeeded run a toast when the schedule asks for it (`notifyOnRun`). Test runs the
 * dialog reports itself are skipped; one left running in the background is always reported.
 *
 * The toast actions only navigate: a toast outlives the page that published it (routes remount per path), so it
 * must not call that page's state setters. Fix opens the automation's page, which opens the edit dialog.
 */
export const useScheduleRunToasts = ({ enabled = true }: ScheduleRunToastsOptions = {}) => {
    const { t } = useTranslation('chat');
    const { publishToast } = useToaster();
    const navigate = useNavigate();
    const { data } = useQuery(GET_CONVERSATION_SCHEDULES, {
        skip: !enabled,
        pollInterval: SCHEDULE_RUNS_UPDATE_INTERVAL,
        skipPollAttempt: skipPollAttemptWhenHidden,
    });

    const initializedRef = useRef(false);
    const autoPausedRef = useRef<Record<string, boolean>>({});
    useEffect(() => {
        const schedules = data?.agentConversationSchedules;
        if (!schedules) return;
        const firstLoad = !initializedRef.current;
        initializedRef.current = true;
        const seen = readSeen();
        let changed = false;

        for (const schedule of schedules) {
            const wasAutoPaused = autoPausedRef.current[schedule.id];
            autoPausedRef.current[schedule.id] = Boolean(schedule.autoPaused);

            // lastRunAt is set when a run finishes; while one runs it is still the previous run's
            if (!schedule.lastRunAt || schedule.lastRunStatus === 'running') continue;
            if (!isNewer(schedule.lastRunAt, seen[schedule.id])) continue;
            seen[schedule.id] = schedule.lastRunAt;
            changed = true;
            if (firstLoad) continue;

            // a run finished after the test started is the test run; an earlier one is a scheduled run as usual
            let testRunInBackground = false;
            const watched = getWatchedTestRun(schedule.id);
            const isTestRun =
                (watched?.state === 'watching' || watched?.state === 'background') &&
                isNewer(schedule.lastRunAt, watched.previousLastRunAt ?? undefined);
            if (watched?.state === 'watching' && isTestRun) continue;
            if (watched?.state === 'reported') {
                forgetWatchedTestRun(schedule.id);
                if (watched.lastRunAt === schedule.lastRunAt) continue;
            }
            if (watched?.state === 'background' && isTestRun) {
                forgetWatchedTestRun(schedule.id);
                testRunInBackground = true;
            }

            const openChat = {
                label: t('automations.toasts.openChat'),
                onClick: () => navigate(url(CONVERSATION_PATH, { conversationId: schedule.conversationId })),
            };
            if (schedule.autoPaused && wasAutoPaused === false) {
                publishToast({
                    title: t('automations.toasts.autoPausedTitle', { name: schedule.name }),
                    description: t('automations.toasts.autoPausedText', {
                        error: runErrorMessage(t, schedule.lastRunError || 'RUN_FAILED'),
                    }),
                    status: 'info',
                    persistent: true,
                    action: {
                        label: t('automations.toasts.fix'),
                        onClick: () =>
                            navigate(url(AUTOMATION_PATH, { scheduleId: schedule.id }), { state: EDIT_SCHEDULE_STATE }),
                    },
                });
            } else if (schedule.lastRunStatus === 'failed') {
                publishToast({
                    title: t('automations.toasts.failed', { name: schedule.name }),
                    description: runErrorMessage(t, schedule.lastRunError || 'RUN_FAILED'),
                    status: 'info',
                    action: openChat,
                });
            } else if (schedule.lastRunStatus === 'succeeded' && (schedule.notifyOnRun || testRunInBackground)) {
                publishToast({
                    title: t('automations.toasts.ran', { name: schedule.name }),
                    description: schedule.conversationTitle
                        ? t('automations.toasts.ranText', { chat: schedule.conversationTitle })
                        : t('automations.toasts.ranTextUntitled'),
                    status: 'info',
                    action: openChat,
                });
            }
        }
        if (changed) writeSeen(seen);
    }, [data]);
};
